import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Coins,
  ChevronDown,
  ChevronUp,
  Server,
  Flame,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * CreditTrackerCard
 *
 * Shows current month usage for both Firecrawl (credits) and Apify (USD)
 * as progress bars with cap context. The card expands to reveal the most
 * recent paid scrape jobs (each row is a "transaction": date/time/cost).
 */
export default function CreditTrackerCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data: payload } = await api.get("/system/credit-tracker", {
        params: { limit: 60 },
      });
      setData(payload);
    } catch (e) {
      if (!silent)
        toast.error("Failed to load credit tracker", {
          description: formatApiError(e),
        });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Force-refresh: busts the Apify 5-min cache AND syncs Firecrawl's real
  // remaining credits from the vendor API. Use after a top-up / cycle reset.
  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: res } = await api.post("/system/credit-tracker/refresh");
      const fc = res?.firecrawl || {};
      if (fc.synced) {
        toast.success("Credits refreshed", {
          description: `Firecrawl: ${fc.remaining_credits} remaining · Apify: $${(res?.apify?.remaining_usd ?? 0).toFixed(2)} remaining`,
        });
      } else if (fc.error) {
        toast.warning("Apify refreshed — Firecrawl sync failed", { description: fc.error });
      }
      await load({ silent: true });
    } catch (e) {
      toast.error("Refresh failed", { description: formatApiError(e) });
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load({ silent: true }), 30000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-white p-5"
        data-testid="credit-tracker-card"
      >
        <Skeleton className="h-5 w-44 mb-3" />
        <Skeleton className="h-3 w-full mb-4" />
        <Skeleton className="h-3 w-full" />
      </div>
    );
  }
  if (!data) return null;

  const fc = data.firecrawl || {};
  const ap = data.apify || {};
  const transactions = data.transactions || [];

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-5"
      data-testid="credit-tracker-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5" /> Scraper credits
          </div>
          <h3 className="text-lg font-semibold text-slate-900 font-display mt-0.5">
            Apify &amp; Firecrawl usage
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={forceRefresh}
          disabled={refreshing}
          className="text-slate-500 hover:text-slate-900"
          data-testid="credit-tracker-refresh"
          aria-label="Sync live from vendor APIs"
          title="Sync live from vendor APIs (bust cache + pull Firecrawl remaining)"
        >
          {refreshing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <UsageBar
          icon={Flame}
          provider="Firecrawl"
          configured={fc.configured}
          usedLabel={`${fmtNumber(fc.credits_used)} credits`}
          capLabel={`${fmtNumber(fc.credit_cap)} cap`}
          remaining={`${fmtNumber(fc.credits_remaining)} remaining`}
          pct={fc.pct}
          subtext={
            fc.source === "vendor_api" && fc.last_synced_at
              ? `Live · synced ${fmtRelative(fc.last_synced_at)}`
              : fc.month
                ? `Month: ${fc.month} · local counter`
                : ""
          }
          warning={fc.disabled_reason === "credit_threshold" ? "Auto-paused — over 80% used" : ""}
          testid="credit-tracker-firecrawl"
        />
        <UsageBar
          icon={Server}
          provider="Apify"
          configured={ap.configured}
          usedLabel={`$${fmtUsd(ap.monthly_usage_usd)}`}
          capLabel={`$${fmtUsd(ap.monthly_cap_usd)} cap`}
          remaining={`$${fmtUsd(ap.remaining_usd)} remaining`}
          pct={ap.pct}
          subtext={fmtCycle(ap.cycle_start, ap.cycle_end)}
          warning={ap.error || ""}
          testid="credit-tracker-apify"
        />
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-4 w-full flex items-center justify-between text-xs font-medium text-slate-600 hover:text-slate-900 border-t border-slate-200 pt-3 transition-colors"
        data-testid="credit-tracker-toggle"
      >
        <span className="inline-flex items-center gap-1.5">
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          {expanded ? "Hide recent transactions" : "Show recent transactions"}
        </span>
        <span className="text-slate-400 tabular-nums">
          {transactions.length} entries
        </span>
      </button>

      {expanded ? (
        <div
          className="mt-2 max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100"
          data-testid="credit-tracker-transactions"
        >
          {transactions.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-500 text-center">
              No paid scraping jobs yet this period.
            </div>
          ) : (
            transactions.map((t) => <TransactionRow key={t.id} tx={t} />)
          )}
        </div>
      ) : null}
    </div>
  );
}

function UsageBar({
  icon: Icon,
  provider,
  configured,
  usedLabel,
  capLabel,
  remaining,
  pct = 0,
  subtext,
  warning,
  testid,
}) {
  const safePct = Math.min(1, Math.max(0, Number(pct) || 0));
  const widthPct = Math.round(safePct * 100);
  let barColor = "bg-emerald-500";
  if (safePct >= 0.9) barColor = "bg-red-500";
  else if (safePct >= 0.7) barColor = "bg-amber-500";
  else if (safePct >= 0.4) barColor = "bg-sky-500";

  return (
    <div
      className="rounded-md border border-slate-200 bg-slate-50/50 px-3 py-3"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
          <Icon className="h-4 w-4 text-slate-500" />
          {provider}
        </div>
        {!configured ? (
          <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
            Not configured
          </span>
        ) : null}
      </div>
      <div className="mt-2 text-xs text-slate-600 flex items-center justify-between tabular-nums">
        <span className="font-semibold text-slate-900">{usedLabel}</span>
        <span>/ {capLabel}</span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${widthPct}%` }}
          aria-label={`${provider} usage ${widthPct}%`}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500">
        <span>{remaining}</span>
        <span>{widthPct}%</span>
      </div>
      {subtext ? (
        <div className="mt-1 text-[11px] text-slate-400 truncate">{subtext}</div>
      ) : null}
      {warning ? (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span className="break-words">{warning}</span>
        </div>
      ) : null}
    </div>
  );
}

function TransactionRow({ tx }) {
  const when = useMemo(
    () => fmtDateTime(tx.finished_at || tx.started_at),
    [tx.started_at, tx.finished_at],
  );
  const costLabel = tx.firecrawl_credits
    ? `${tx.firecrawl_credits} credits`
    : tx.apify_cost_usd
    ? `$${fmtUsd(tx.apify_cost_usd)}`
    : "-";
  return (
    <div
      className="grid grid-cols-12 gap-2 px-3 py-2 text-xs items-center"
      data-testid={`credit-tracker-tx-${tx.id}`}
    >
      <div className="col-span-5 sm:col-span-4 min-w-0">
        <div className="font-medium text-slate-900 truncate">
          {labelForType(tx.type)}
        </div>
        {tx.target_label ? (
          <div className="text-[11px] text-slate-500 truncate">{tx.target_label}</div>
        ) : null}
      </div>
      <div className="col-span-3 text-slate-500 tabular-nums truncate">{when}</div>
      <div className="col-span-2 text-slate-600 capitalize">
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${
            tx.provider === "firecrawl"
              ? "bg-orange-50 text-orange-700 border-orange-200"
              : tx.provider === "apify"
              ? "bg-sky-50 text-sky-700 border-sky-200"
              : "bg-slate-50 text-slate-700 border-slate-200"
          }`}
        >
          {tx.provider}
        </span>
      </div>
      <div className="col-span-2 sm:col-span-3 text-right font-semibold text-slate-900 tabular-nums truncate">
        {costLabel}
      </div>
    </div>
  );
}

function fmtNumber(n) {
  if (n === undefined || n === null) return "—";
  return Number(n).toLocaleString();
}
function fmtUsd(n) {
  const v = Number(n) || 0;
  return v < 1 ? v.toFixed(4) : v.toFixed(2);
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(
      undefined,
      { hour: "2-digit", minute: "2-digit" },
    )}`;
  } catch {
    return "—";
  }
}
function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch {
    return "—";
  }
}
function fmtCycle(start, end) {
  if (!start || !end) return "";
  try {
    const s = new Date(start);
    const e = new Date(end);
    const fmt = (d) =>
      d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `Cycle: ${fmt(s)} → ${fmt(e)}`;
  } catch {
    return "";
  }
}
function labelForType(t) {
  const map = {
    enrich_realtor: "Realtor enrichment",
    enrich_all: "Bulk realtor enrichment",
    enrich_personal_websites: "Personal-site enrichment",
    enrich_brokerages: "Brokerage enrichment",
    scrape_brokerage: "Brokerage scrape",
    scrape_all: "Scrape all brokerages",
    find_brokerages: "Find brokerages",
    realtor_ca_import: "Realtor.ca import",
    openhouse_scan: "Open-house scan",
  };
  return map[t] || t || "Scraping job";
}
